import React, { useMemo } from "react"
import Link from "next/link"

import { ActionToolbar, Button, type ToolbarItem } from "@coval/ui"
import { Puzzle, RefreshCw } from "lucide-react"

import { NewResourceButton } from "@/components/ResourceActionButton"
import type { HeaderAction } from "@/contexts/HeaderActionsContext"
import { useSetHeaderActions } from "@/hooks/useSetHeaderActions"

/** Simulations links to its Scheduled and Templates siblings beside the create action. */
export function RunsHeaderActions({
  onSimulate,
  scheduledHref,
  templatesHref,
}: {
  onSimulate: () => void
  scheduledHref: string
  templatesHref: string
}) {
  const action = useMemo<HeaderAction>(() => {
    const linkItem = (
      id: string,
      label: string,
      href: string,
      icon: React.ReactNode,
      priority: number,
    ): ToolbarItem => {
      const control = (compact: boolean, measure = false) => (
        <Button
          asChild
          variant="secondary"
          icon={compact}
          tooltip={compact ? label : undefined}
          // This header sits below the sticky navbar, so labels open beneath it.
          tooltipProps={{ side: "bottom" }}
          aria-label={label}
          data-testid={measure ? undefined : `runs-${id}-button`}
        >
          <Link href={href}>
            {icon}
            {!compact && label}
          </Link>
        </Button>
      )
      return {
        type: "custom",
        id,
        priority,
        full: control(false),
        compact: control(true),
        measureFull: control(false, true),
        measureCompact: control(true, true),
      }
    }
    const items: ToolbarItem[] = [
      linkItem("scheduled", "Scheduled", scheduledHref, <RefreshCw />, 0),
      linkItem("templates", "Templates", templatesHref, <Puzzle />, 1),
      {
        type: "custom",
        id: "new-simulation",
        priority: 2,
        full: (
          <NewResourceButton
            resourceName="Simulation"
            onClick={onSimulate}
            data-testid="runs-new-simulation-button"
          />
        ),
        compact: (
          <NewResourceButton
            compact
            resourceName="Simulation"
            onClick={onSimulate}
            data-testid="runs-new-simulation-button"
          />
        ),
        measureFull: <NewResourceButton resourceName="Simulation" />,
        measureCompact: <NewResourceButton compact resourceName="Simulation" />,
      },
    ]
    return {
      type: "custom",
      responsive: true,
      node: (
        <ActionToolbar
          overflowBehavior="hide"
          items={items}
          data-testid="runs-header-actions"
        />
      ),
    }
  }, [onSimulate, scheduledHref, templatesHref])
  useSetHeaderActions(action)
  return null
}
